import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Campaign as CampaignIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  SmartToy,
  Lightbulb,
  Psychology,
  Share as ShareIcon,
  ExpandMore,
  ExpandLess,
  MenuBook,
} from '@mui/icons-material';
import { campaignsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import ConfirmDialog from '../components/ConfirmDialog';
import { FormDialog, PageHeader, StatusChip } from '../components/primitives';

const Campaigns = () => {
  const { showError, showSuccess, showWarning } = useToast();
  const { clients } = useClients();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showAdvancedAi, setShowAdvancedAi] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    campaign_type: 'engagement',
    client_id: '',
    target_audience: '',
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    campaignId: null,
  });
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const data = await campaignsAPI.getCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (campaignId) => {
    try {
      await campaignsAPI.activateCampaign(campaignId);
      showSuccess('Campaign activated successfully!');
      fetchCampaigns();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to activate campaign',
      );
    }
  };

  const handlePause = async (campaignId) => {
    try {
      await campaignsAPI.pauseCampaign(campaignId);
      showSuccess('Campaign paused successfully!');
      fetchCampaigns();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to pause campaign',
      );
    }
  };

  const handleDelete = (campaignId) => {
    setDeleteConfirm({ open: true, campaignId });
  };

  const handleConfirmDelete = async () => {
    const campaignId = deleteConfirm.campaignId;
    if (!campaignId) return;
    setDeleteBusy(true);
    try {
      await campaignsAPI.deleteCampaign(campaignId);
      showSuccess('Campaign deleted successfully!');
      setDeleteConfirm({ open: false, campaignId: null });
      fetchCampaigns();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to delete campaign',
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleEdit = (campaign) => {
    setSelectedCampaign(campaign);
    setForm({
      name: campaign.name,
      description: campaign.description || '',
      campaign_type: campaign.campaign_type,
      client_id: campaign.client_id,
      target_audience: campaign.target_audience || '',
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selectedCampaign) {
        await campaignsAPI.updateCampaign(selectedCampaign.id, form);
        showSuccess('Campaign updated successfully!');
      } else {
        await campaignsAPI.createCampaign(form);
        showSuccess('Campaign created successfully!');
      }
      setOpenDialog(false);
      setSelectedCampaign(null);
      setForm({
        name: '',
        description: '',
        campaign_type: 'engagement',
        client_id: '',
        target_audience: '',
      });
      fetchCampaigns();
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to save campaign',
      );
    }
  };

  const getCampaignTypeColor = (type) => {
    switch (type) {
      case 'awareness':
        return 'primary';
      case 'engagement':
        return 'secondary';
      case 'conversion':
        return 'success';
      case 'brand_building':
        return 'info';
      default:
        return 'default';
    }
  };

  // AI Campaign Optimization Component
  const AICampaignOptimization = () => {
    const [aiOpen, setAiOpen] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [optimizationData, setOptimizationData] = useState(null);
    const [aiForm, setAiForm] = useState({
      client_id: '',
      campaign_type: 'engagement',
      target_audience: '',
      budget: 1000,
      duration_days: 30,
    });

    const handleGetOptimization = async () => {
      if (!aiForm.client_id) {
        showWarning('Please select a client');
        return;
      }

      setAiLoading(true);
      try {
        const response = await campaignsAPI.getAICampaignOptimization(
          parseInt(aiForm.client_id),
          aiForm.campaign_type,
          aiForm.target_audience || null,
          aiForm.budget || null,
          aiForm.duration_days || null
        );
        setOptimizationData(response);
      } catch (error) {
        console.error('Error getting AI optimization:', error);
        showError('Failed to get AI optimization');
      } finally {
        setAiLoading(false);
      }
    };

    const handleCreateAutomatedCampaign = async () => {
      if (!aiForm.client_id) {
        showWarning('Please select a client');
        return;
      }

      setAiLoading(true);
      try {
        const response = await campaignsAPI.createAIAutomatedCampaign(
          parseInt(aiForm.client_id),
          aiForm.campaign_type,
          aiForm.target_audience || null,
          aiForm.budget || null,
          aiForm.duration_days || null
        );
        showSuccess('AI-powered campaign created successfully!');
        setAiOpen(false);
        fetchCampaigns(); // Refresh campaigns list
      } catch (error) {
        console.error('Error creating automated campaign:', error);
        showError('Failed to create automated campaign');
      } finally {
        setAiLoading(false);
      }
    };

    return (
      <>
        <Button
          variant="outlined"
          startIcon={<SmartToy />}
          onClick={() => setAiOpen(true)}
          sx={{
            borderColor: '#30363d',
            color: '#c9d1d9',
            minWidth: 'fit-content',
          }}
        >
          AI recommendations
        </Button>

        <Dialog
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToy />
              AI campaign recommendations (Advanced)
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 4, overflow: 'auto' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Optional. Returns strategy suggestions and estimated projections — not live ads
              platform data. Prefer <strong>New Campaign</strong> for the normal create path.
              See the{' '}
              <Button
                component={RouterLink}
                to="/docs/campaigns"
                size="small"
                sx={{ textTransform: 'none', p: 0, minWidth: 0, verticalAlign: 'baseline' }}
              >
                Campaigns guide
              </Button>
              .
            </Alert>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: 800,
                mx: 'auto',
                width: '100%',
              }}
            >
              {/* Form Section */}
              <Box
                sx={{
                  background:
                    'linear-gradient(135deg, rgba(13, 17, 23, 0.8) 0%, rgba(22, 27, 34, 0.8) 100%)',
                  border: '1px solid rgba(48, 54, 61, 0.4)',
                  borderRadius: 4,
                  p: 4,
                  mb: 6,
                  mt: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, #667eea, #764ba2)',
                  },
                }}
              >
                {/* Section Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#f0f6fc',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CampaignIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    Campaign Parameters
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7d8590' }}>
                    Configure your campaign settings for AI optimization
                  </Typography>
                </Box>

                {/* Form Fields */}
                <Grid container spacing={4} justifyContent="center">
                  {/* Client Selection */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                      >
                        Client
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={aiForm.client_id}
                          onChange={(e) =>
                            setAiForm({
                              ...aiForm,
                              client_id: e.target.value,
                            })
                          }
                          displayEmpty
                          sx={{
                            color: '#f0f6fc',
                            bgcolor: 'rgba(13, 17, 23, 0.6)',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(48, 54, 61, 0.4)',
                              borderWidth: '2px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                            },
                            '& .MuiSelect-icon': {
                              color: '#7d8590',
                            },
                            '& .MuiSelect-select': {
                              py: 1.5,
                            },
                          }}
                        >
                          <MenuItem value="" disabled sx={{ color: '#7d8590' }}>
                            Select a client
                          </MenuItem>
                          {clients.map((client) => (
                            <MenuItem
                              key={client.id}
                              value={client.id}
                              sx={{
                                color: '#f0f6fc',
                                '&:hover': {
                                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                                },
                              }}
                            >
                              {client.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Campaign Type */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                      >
                        Campaign Type
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          value={aiForm.campaign_type}
                          onChange={(e) =>
                            setAiForm({
                              ...aiForm,
                              campaign_type: e.target.value,
                            })
                          }
                          sx={{
                            color: '#f0f6fc',
                            bgcolor: 'rgba(13, 17, 23, 0.6)',
                            borderRadius: 2,
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(48, 54, 61, 0.4)',
                              borderWidth: '2px',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#667eea',
                            },
                            '& .MuiSelect-icon': {
                              color: '#7d8590',
                            },
                            '& .MuiSelect-select': {
                              py: 1.5,
                            },
                          }}
                        >
                          <MenuItem
                            value="awareness"
                            sx={{
                              color: '#f0f6fc',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                              },
                            }}
                          >
                            Awareness
                          </MenuItem>
                          <MenuItem
                            value="engagement"
                            sx={{
                              color: '#f0f6fc',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                              },
                            }}
                          >
                            Engagement
                          </MenuItem>
                          <MenuItem
                            value="conversion"
                            sx={{
                              color: '#f0f6fc',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                              },
                            }}
                          >
                            Conversion
                          </MenuItem>
                          <MenuItem
                            value="brand_building"
                            sx={{
                              color: '#f0f6fc',
                              '&:hover': {
                                bgcolor: 'rgba(102, 126, 234, 0.1)',
                              },
                            }}
                          >
                            Brand Building
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Target Audience */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                      >
                        Target Audience
                      </Typography>
                      <TextField
                        fullWidth
                        value={aiForm.target_audience}
                        onChange={(e) =>
                          setAiForm({
                            ...aiForm,
                            target_audience: e.target.value,
                          })
                        }
                        placeholder="e.g., Young professionals, 25-35"
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(13, 17, 23, 0.6)',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: 'rgba(48, 54, 61, 0.4)',
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                            '& input': {
                              color: '#f0f6fc',
                              py: 1.5,
                              '&::placeholder': {
                                color: '#7d8590',
                                opacity: 0.7,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Budget */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                      >
                        Budget
                      </Typography>
                      <TextField
                        type="number"
                        fullWidth
                        value={aiForm.budget}
                        onChange={(e) =>
                          setAiForm({
                            ...aiForm,
                            budget: parseInt(e.target.value),
                          })
                        }
                        placeholder="1000"
                        variant="outlined"
                        InputProps={{
                          startAdornment: (
                            <Box
                              sx={{
                                color: '#7d8590',
                                mr: 1,
                                fontWeight: 500,
                              }}
                            >
                              $
                            </Box>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(13, 17, 23, 0.6)',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: 'rgba(48, 54, 61, 0.4)',
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                            '& input': {
                              color: '#f0f6fc',
                              py: 1.5,
                              '&::placeholder': {
                                color: '#7d8590',
                                opacity: 0.7,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>

                  {/* Duration */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'relative' }}>
                      <Typography
                        variant="body2"
                        sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                      >
                        Duration
                      </Typography>
                      <TextField
                        type="number"
                        fullWidth
                        value={aiForm.duration_days}
                        onChange={(e) =>
                          setAiForm({
                            ...aiForm,
                            duration_days: parseInt(e.target.value),
                          })
                        }
                        placeholder="30"
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <Box
                              sx={{
                                color: '#7d8590',
                                ml: 1,
                                fontWeight: 500,
                              }}
                            >
                              days
                            </Box>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(13, 17, 23, 0.6)',
                            borderRadius: 2,
                            '& fieldset': {
                              borderColor: 'rgba(48, 54, 61, 0.4)',
                              borderWidth: '2px',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(102, 126, 234, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#667eea',
                            },
                            '& input': {
                              color: '#f0f6fc',
                              py: 1.5,
                              '&::placeholder': {
                                color: '#7d8590',
                                opacity: 0.7,
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 4,
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  py: 2,
                  width: '100%',
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Lightbulb />}
                  onClick={handleGetOptimization}
                  disabled={aiLoading}
                  sx={{
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    px: 5,
                    py: 2.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: 'rgba(48, 54, 61, 0.4)',
                      color: '#7d8590',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.3s ease',
                    minWidth: 200,
                  }}
                >
                  {aiLoading
                    ? 'Getting Recommendations...'
                    : 'Get recommendations'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Psychology />}
                  onClick={handleCreateAutomatedCampaign}
                  disabled={aiLoading}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    fontWeight: 600,
                    px: 5,
                    py: 2.5,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '1rem',
                    borderWidth: '2px',
                    '&:hover': {
                      borderColor: '#5a6fd8',
                      color: '#5a6fd8',
                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      borderColor: 'rgba(48, 54, 61, 0.4)',
                      color: '#7d8590',
                      transform: 'none',
                    },
                    transition: 'all 0.3s ease',
                    minWidth: 200,
                  }}
                >
                  {aiLoading
                    ? 'Creating…'
                    : 'Create campaign with AI'}
                </Button>
              </Box>

              {/* Loading State */}
              {aiLoading && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 3,
                    background: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: 3,
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                    width: '100%',
                  }}
                >
                  <CircularProgress size={24} sx={{ color: '#667eea' }} />
                  <Typography sx={{ color: '#f0f6fc', fontWeight: 500 }}>
                    AI is analyzing your campaign parameters...
                  </Typography>
                </Box>
              )}

              {/* Optimization Results */}
              {optimizationData && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      color: '#f0f6fc',
                    }}
                  >
                    <Box
                      sx={{
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        borderRadius: 2,
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Lightbulb sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                    AI Optimization Results
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7d8590', mb: 3 }}>
                    Here are your AI-generated campaign recommendations and
                    strategies.
                  </Typography>

                  {/* Organic Strategy Overview */}
                  {aiForm.budget === 0 &&
                    optimizationData.budget_allocation?.strategy_type ===
                      'organic_growth' && (
                      <Box
                        sx={{
                          background: 'rgba(13, 17, 23, 0.6)',
                          border: '1px solid rgba(48, 54, 61, 0.4)',
                          borderRadius: 3,
                          p: 3,
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#f0f6fc',
                          }}
                        >
                          <Box
                            sx={{
                              background:
                                'linear-gradient(135deg, #28a745, #20c997)',
                              borderRadius: 2,
                              p: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <TrendingUpIcon
                              sx={{ color: 'white', fontSize: 16 }}
                            />
                          </Box>
                          Organic Growth Strategy
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#7d8590', mb: 0.5 }}
                            >
                              Time Commitment
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#f0f6fc', fontWeight: 500 }}
                            >
                              {optimizationData.budget_allocation
                                ?.time_commitment || '2-4 hours per week'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#7d8590', mb: 0.5 }}
                            >
                              Expected Timeline
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{ color: '#f0f6fc', fontWeight: 500 }}
                            >
                              {optimizationData.budget_allocation
                                ?.expected_timeline ||
                                '3-6 months for significant growth'}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Typography
                          variant="body2"
                          sx={{ color: '#7d8590', mb: 1, fontWeight: 500 }}
                        >
                          Key Recommendations:
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {optimizationData.budget_allocation?.recommendations?.map(
                            (rec, index) => (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: '#28a745',
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: '#f0f6fc' }}
                                >
                                  {rec}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>
                    )}

                  {/* Platform Strategies */}
                  {aiForm.budget === 0 &&
                    optimizationData.budget_allocation?.platform_strategies && (
                      <Box
                        sx={{
                          background: 'rgba(13, 17, 23, 0.6)',
                          border: '1px solid rgba(48, 54, 61, 0.4)',
                          borderRadius: 3,
                          p: 3,
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#f0f6fc',
                          }}
                        >
                          <ShareIcon sx={{ color: '#667eea' }} />
                          Platform Strategies
                        </Typography>

                        <Grid container spacing={2}>
                          {Object.entries(
                            optimizationData.budget_allocation
                              .platform_strategies
                          ).map(([platform, strategy]) => (
                            <Grid item xs={12} md={6} key={platform}>
                              <Box
                                sx={{
                                  background: 'rgba(102, 126, 234, 0.1)',
                                  border: '1px solid rgba(102, 126, 234, 0.2)',
                                  borderRadius: 2,
                                  p: 2,
                                  height: '100%',
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: '#f0f6fc',
                                    fontWeight: 600,
                                    mb: 1,
                                  }}
                                >
                                  {platform}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', mb: 1 }}
                                >
                                  Time Investment: {strategy.time_investment}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#7d8590',
                                    mb: 1,
                                    fontWeight: 500,
                                  }}
                                >
                                  Content Focus:
                                </Typography>
                                <Box sx={{ mb: 1 }}>
                                  {strategy.content_focus
                                    ?.slice(0, 3)
                                    .map((content, index) => (
                                      <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{
                                          color: '#7d8590',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        • {content}
                                      </Typography>
                                    ))}
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{ color: '#7d8590', fontWeight: 500 }}
                                >
                                  Growth Tactics:
                                </Typography>
                                <Box>
                                  {strategy.growth_tactics
                                    ?.slice(0, 2)
                                    .map((tactic, index) => (
                                      <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{
                                          color: '#7d8590',
                                          fontSize: '0.8rem',
                                        }}
                                      >
                                        • {tactic}
                                      </Typography>
                                    ))}
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                  {/* Performance Metrics */}
                  {optimizationData.expected_performance && (
                    <Box
                      sx={{
                        background: 'rgba(13, 17, 23, 0.6)',
                        border: '1px solid rgba(48, 54, 61, 0.4)',
                        borderRadius: 3,
                        p: 3,
                        mb: 3,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: '#f0f6fc',
                        }}
                      >
                        <TrendingUpIcon sx={{ color: '#667eea' }} />
                        {aiForm.budget === 0
                          ? 'Organic Growth Projections'
                          : 'Expected Performance'}
                      </Typography>

                      <Grid container spacing={3}>
                        {aiForm.budget === 0 ? (
                          // Organic metrics
                          <>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Monthly Reach
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {optimizationData.expected_performance.expected_monthly_reach?.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Engagement Rate
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {(
                                  (optimizationData.expected_performance
                                    .expected_engagement_rate || 0) * 100
                                ).toFixed(1)}
                                %
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Follower Growth Rate
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {(
                                  (optimizationData.expected_performance
                                    .expected_follower_growth_rate || 0) * 100
                                ).toFixed(1)}
                                %
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Confidence Level
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {optimizationData.expected_performance
                                  .confidence_level || '70%'}
                              </Typography>
                            </Grid>
                          </>
                        ) : (
                          // Paid metrics
                          <>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Expected Reach
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {optimizationData.expected_performance.expected_reach?.toLocaleString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#7d8590', mb: 0.5 }}
                              >
                                Engagement Rate
                              </Typography>
                              <Typography
                                variant="h5"
                                sx={{ color: '#f0f6fc', fontWeight: 700 }}
                              >
                                {(
                                  (optimizationData.expected_performance
                                    .expected_engagement_rate || 0) * 100
                                ).toFixed(1)}
                                %
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>

                      {/* Growth Timeline for Organic */}
                      {aiForm.budget === 0 &&
                        optimizationData.expected_performance
                          .growth_timeline && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="body2"
                              sx={{ color: '#7d8590', mb: 2, fontWeight: 500 }}
                            >
                              Growth Timeline:
                            </Typography>
                            <Grid container spacing={2}>
                              {Object.entries(
                                optimizationData.expected_performance
                                  .growth_timeline
                              ).map(([month, description]) => (
                                <Grid item xs={12} md={6} key={month}>
                                  <Box
                                    sx={{
                                      background: 'rgba(102, 126, 234, 0.1)',
                                      border:
                                        '1px solid rgba(102, 126, 234, 0.2)',
                                      borderRadius: 2,
                                      p: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#667eea',
                                        fontWeight: 600,
                                        mb: 0.5,
                                      }}
                                    >
                                      {month.replace('_', ' ').toUpperCase()}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#f0f6fc',
                                        fontSize: '0.9rem',
                                      }}
                                    >
                                      {description}
                                    </Typography>
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="Campaigns"
        subtitle="Create a campaign in three steps — AI recommendations stay under Advanced"
        icon={<CampaignIcon sx={{ fontSize: 28 }} />}
        actions={
          <>
            <Button
              component={RouterLink}
              to="/docs/campaigns"
              variant="text"
              startIcon={<MenuBook />}
              sx={{ color: '#8b949e' }}
            >
              How campaigns work
            </Button>
            <Button
              variant="text"
              endIcon={showAdvancedAi ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowAdvancedAi((v) => !v)}
              sx={{ color: '#8b949e' }}
            >
              {showAdvancedAi ? 'Hide advanced' : 'Advanced'}
            </Button>
            {showAdvancedAi ? <AICampaignOptimization /> : null}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                bgcolor: '#58a6ff',
                '&:hover': { bgcolor: '#1f6feb' },
              }}
            >
              New Campaign
            </Button>
          </>
        }
      />

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>3-step create:</strong> (1) Click New Campaign → (2) Name, client, type, audience →
        (3) Activate when ready. AI recommendations are optional under <strong>Advanced</strong>.{' '}
        <Button
          component={RouterLink}
          to="/docs/campaigns"
          size="small"
          sx={{ textTransform: 'none', verticalAlign: 'baseline' }}
        >
          Full guide
        </Button>
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {campaigns.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'rgba(13, 17, 23, 0.6)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(48, 54, 61, 0.4)',
          }}
        >
          <CampaignIcon sx={{ fontSize: 64, color: '#7d8590', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 1 }}>
            No campaigns yet
          </Typography>
          <Typography variant="body2" sx={{ color: '#7d8590', mb: 1 }}>
            A campaign is a client-scoped plan (name, type, audience, status) — not Instagram
            follow/unfollow automation.
          </Typography>
          <Typography variant="body2" sx={{ color: '#7d8590', mb: 3 }}>
            1. New Campaign → 2. Fill details → 3. Activate when ready. Optional AI tips live under
            Advanced.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                bgcolor: '#58a6ff',
                '&:hover': { bgcolor: '#1f6feb' },
              }}
            >
              Create Campaign
            </Button>
            <Button
              component={RouterLink}
              to="/docs/campaigns"
              variant="outlined"
              startIcon={<MenuBook />}
            >
              Campaigns guide
            </Button>
          </Stack>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} md={6} lg={4} key={campaign.id}>
              <Card
                sx={{
                  bgcolor: 'rgba(13, 17, 23, 0.6)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(48, 54, 61, 0.4)',
                  '&:hover': {
                    border: '1px solid rgba(88, 166, 255, 0.4)',
                    boxShadow: '0 8px 32px rgba(88, 166, 255, 0.1)',
                  },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: '#f0f6fc', fontWeight: 600 }}
                    >
                      {campaign.name}
                    </Typography>
                    <Box>
                      {campaign.status === 'paused' ? (
                        <StatusChip
                          label="Paused"
                          color="warning"
                          sx={{ mb: 1 }}
                        />
                      ) : (
                        <StatusChip status={campaign.status} sx={{ mb: 1 }} />
                      )}
                      <Chip
                        label={campaign.campaign_type}
                        size="small"
                        color={getCampaignTypeColor(campaign.campaign_type)}
                      />
                    </Box>
                  </Box>

                  {campaign.description && (
                    <Typography
                      variant="body2"
                      sx={{ color: '#7d8590', mb: 2 }}
                    >
                      {campaign.description}
                    </Typography>
                  )}

                  <Box display="flex" alignItems="center" mb={1}>
                    <BusinessIcon
                      sx={{ fontSize: 16, color: '#7d8590', mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ color: '#7d8590' }}>
                      {campaign.client_name ||
                        campaign.client_company ||
                        'Unknown Client'}
                    </Typography>
                  </Box>

                  {campaign.target_audience && (
                    <Box display="flex" alignItems="center" mb={2}>
                      <TrendingUpIcon
                        sx={{ fontSize: 16, color: '#7d8590', mr: 1 }}
                      />
                      <Typography variant="body2" sx={{ color: '#7d8590' }}>
                        {campaign.target_audience}
                      </Typography>
                    </Box>
                  )}

                  {campaign.performance && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid rgba(48, 54, 61, 0.6)',
                        bgcolor: 'rgba(22, 27, 34, 0.5)',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#7d8590', display: 'block', mb: 1 }}
                      >
                        Published performance
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#7d8590' }}>
                            Posts
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                            {campaign.performance.published_posts}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#7d8590' }}>
                            Engagement
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                            {Number(campaign.performance.total_engagement || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#7d8590' }}>
                            Likes
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                            {Number(campaign.performance.likes || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#7d8590' }}>
                            Comments
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                            {Number(campaign.performance.comments || 0).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#7d8590' }}>
                            Shares
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                            {Number(campaign.performance.shares || 0).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" mb={3}>
                    <ScheduleIcon
                      sx={{ fontSize: 16, color: '#7d8590', mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ color: '#7d8590' }}>
                      Created{' '}
                      {new Date(campaign.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={1}>
                    {campaign.status === 'active' ? (
                      <Tooltip title="Pause Campaign">
                        <IconButton
                          size="small"
                          onClick={() => handlePause(campaign.id)}
                          sx={{ color: '#f0f6fc' }}
                        >
                          <PauseIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Activate Campaign">
                        <IconButton
                          size="small"
                          onClick={() => handleActivate(campaign.id)}
                          sx={{ color: '#f0f6fc' }}
                        >
                          <PlayIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit Campaign">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(campaign)}
                        sx={{ color: '#f0f6fc' }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Campaign">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(campaign.id)}
                        sx={{ color: '#f85149' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Campaign Dialog */}
      <FormDialog
        open={openDialog}
        maxWidth="md"
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CampaignIcon sx={{ color: '#58a6ff' }} />
            {selectedCampaign ? 'Edit Campaign' : 'New Campaign'}
          </Box>
        }
        submitLabel={selectedCampaign ? 'Update' : 'Create'}
        onSubmit={handleSave}
        onClose={() => {
          setOpenDialog(false);
          setSelectedCampaign(null);
          setForm({
            name: '',
            description: '',
            campaign_type: 'engagement',
            client_id: '',
            target_audience: '',
          });
        }}
      >
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Campaign Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mb: 3 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={3}
            sx={{ mb: 3 }}
          />
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Campaign Type</InputLabel>
            <Select
              value={form.campaign_type}
              label="Campaign Type"
              onChange={(e) =>
                setForm({ ...form, campaign_type: e.target.value })
              }
            >
              <MenuItem value="awareness">Brand Awareness</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
              <MenuItem value="conversion">Sales Conversion</MenuItem>
              <MenuItem value="brand_building">Brand Building</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={form.client_id}
              label="Client"
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            >
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name || client.company || `Client ${client.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Target Audience"
            value={form.target_audience}
            onChange={(e) =>
              setForm({ ...form, target_audience: e.target.value })
            }
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete campaign"
        message="Are you sure you want to delete this campaign?"
        confirmLabel="Delete"
        loading={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteConfirm({ open: false, campaignId: null });
        }}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

export default Campaigns;
